import {
  Box,
  Button,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { t } from "i18next";
import api from "../API/apiCollection";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { resetState } from "../redux/cart";
import { orderCartDetailsReset } from "../redux/orderCartDetails";
import { deliveryAddressReset } from "../redux/DeliveryAddress";
import { useNavigate } from "react-router";
import { resetStatePromo } from "../redux/Promocode";

// --- DOKU Payment Component (Using loadJokulCheckout) ---

// Define the global function exposed by the DOKU script for type clarity
/**
 * @typedef {function(string): void} LoadJokulCheckout
 */
// const loadJokulCheckout = /** @type {LoadJokulCheckout} */ (window.loadJokulCheckout);

const Doku = ({ amount }) => {
  const settings = useSelector((state) => state?.Settings)?.settings
    ?.payment_gateways_settings;

  // --- Order/State Details (Omitted for brevity, assumed correct) ---
  const orderDetails = useSelector((state) => state.OrderCartDetails)?.orderDetails;
  const delivery_type = useSelector((state) => state.DeliveryAddress)?.deliveryType;
  const deliveryAddress = useSelector((state) => state.DeliveryAddress)?.delivery;
  const date = orderDetails && orderDetails.date;
  const time = orderDetails && orderDetails.slot;
  const address = deliveryAddress !== "" ? deliveryAddress : "";
  const orderNotes = orderDetails.orderNote;
  let promocode = useSelector((state) => state.Promocode);
  let selectedPromoCode = promocode.selectedPromoCode;

  const [order_id, setOrderID] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Load the officially documented DOKU script dynamically
  useEffect(() => {
    // Check if the required function is already available
    if (window.loadJokulCheckout && typeof window.loadJokulCheckout === 'function') {
        setIsScriptLoaded(true);
        return;
    }
    
    // Use the URL pattern provided in your documentation snippet
    const isSandbox = settings?.doku_mode === 'test';
    
    // NOTE: We assume the version 'v1/jokul-checkout-1.0.0.js' is the current standard.
    // If the file version changes, you may need to update this string.
    const scriptSrc = isSandbox
        ? "https://sandbox.doku.com/jokul-checkout-js/v1/jokul-checkout-1.0.0.js" 
        : "https://jokul.doku.com/jokul-checkout-js/v1/jokul-checkout-1.0.0.js"; // Guessing the production domain

    const script = document.createElement('script');
    script.src = scriptSrc;
    script.async = true;

    script.onload = () => {
        // The script registers the global function loadJokulCheckout
        if (window.loadJokulCheckout && typeof window.loadJokulCheckout === 'function') {
            setIsScriptLoaded(true);
            console.log("DOKU Jokul Checkout script loaded successfully.");
        } else {
            console.error("DOKU script loaded, but 'loadJokulCheckout' is not defined.");
            toast.error("DOKU component script failed to initialize.");
        }
    };
    
    script.onerror = () => {
        console.error("Failed to load DOKU script from the server.");
        toast.error("Failed to load DOKU payment library. Check URL or network.");
    };

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [settings?.doku_mode]);

  /**
   * Cleans up Redux state after a successful payment.
   */
  const handleSuccessCleanup = () => {
    dispatch(resetState());
    dispatch(orderCartDetailsReset());
    dispatch(deliveryAddressReset());
    dispatch(resetStatePromo());
  };

  /**
   * Handles actions after the payment is completed.
   * NOTE: For DOKU Snap/Popup, the final status confirmation should come via Webhook.
   * This function handles navigation after the popup closes/redirects.
   */
  const handlePaymentOutcome = async (oid, status = "pending") => {
    setLoading(false);
    
    // Update status in the backend (using the status passed, default to pending if unknown)
    try {
        await api.add_transactions({ orderID: oid, status: status });
    } catch (error) {
        toast.error("Failed to update transaction status in the backend.");
        console.error("Error updating transaction:", error);
    }
    
    // Clean up state if payment was confirmed successful by DOKU's client-side redirect/callback
    // (This part is often tricky with DOKU; rely on Webhooks for true success)
    if (status === "success") {
        handleSuccessCleanup();
    }
    
    // Navigate to the order details page
    navigate("/profile/booking/services/" + oid);
  };
  

  /**
   * Calls the placeOrder API to create a transaction and get the DOKU payment URL.
   */
  const getDOKUPaymentUrl = async () => {
    // PRE-CHECK: Ensure the script is loaded before proceeding
    if (loading || !isScriptLoaded) {
        toast.error("DOKU payment library not loaded yet. Please wait.");
        return;
    }

    setLoading(true);
    
    try {
      // 1. Call your backend API.
      // Your backend must now call DOKU's API (e.g., /checkout) and return the payment URL.
      const result = await api.placeOrder({
        method: "doku", 
        date: date,
        time: time,
        addressId: delivery_type === "Home" ? address?.id : "",
        order_note: orderNotes,
        promo_code_id: selectedPromoCode ? selectedPromoCode.id : "",
      });

      if (result.error === false || result.error === "false") {
        // Ensure your API returns the DOKU URL as 'doku_payment_url'
        const { order_id: newOrderId, doku_payment_url: paymentUrl } = result.data; 
        
        if (paymentUrl) {
            setOrderID(newOrderId);
            
            // 2. Open the DOKU payment window using the correct function and URL
            window.loadJokulCheckout(paymentUrl);
            
            // IMPORTANT: DOKU Snap/Popup doesn't provide client-side callbacks like Midtrans Snap.
            // When the payment process is finished or the user closes the popup, control returns.
            // We set the status to 'pending' and navigate after a brief timeout, relying on
            // DOKU's Webhook to confirm the final status later.
            /*setTimeout(() => {
                 // Assume the order is pending or cancelled when the popup closes/redirects.
                 // The Webhook handles final confirmation.
                 handlePaymentOutcome(newOrderId, "pending"); 
            }, 5000); // Wait 5 seconds to allow DOKU to load/redirect/close
            */

            // IMPORTANT: Keep the component alive but stop the spinner while
            // the user is interacting with the DOKU popup.
            setLoading(false);
        } else {
            toast.error("DOKU payment URL is missing from the API response.");
            setLoading(false);
        }
      } else {
        if (typeof result.message === "object") {
          Object.values(result.message).forEach((e) => {
            toast.error(e);
          });
        } else {
          toast.error(result.message);
        }
        setLoading(false);
      }
    } catch (error) {
      console.error("Error placing order:", error);
      toast.error("Failed to place order. Please try again later.");
      setLoading(false);
    }
  };


  return (
    <Box>
      <Button
        fullWidth
        sx={{ my: 1 }}
        variant="outlined"
        onClick={getDOKUPaymentUrl}
        disabled={loading || !isScriptLoaded} 
      >
        {
          loading 
            ? t("loading") 
            : isScriptLoaded 
              ? `${t("make_a_payment")} ${t("now")}` 
              : t("loading_payment_gateway")
        }
      </Button>
    </Box>
  );
};

export default Doku;